import React, { useState } from "react";
import { useApplication } from "./ApplicationProvider";
import { usePublicConfig } from "./PublicConfigProvider";
import { formatIsoDate } from "../config";
import {
  PROOF_EXTENSIONS,
  REASON_TEXT_MAX,
  WITHDRAWAL_ACK_TEXT,
  WITHDRAWAL_REASONS,
  WRITTEN_NOTICE_TEXT,
  defaultCancellationDeadline,
  withdrawalFormErrors,
  withdrawalInTime,
  withdrawalOpen,
  withdrawalReason,
  type WithdrawalSummary,
} from "../withdrawal";

/** Who is asking + their name — shared by the withdraw form and the withdrawn screen. */
export function RequesterFields({
  idPrefix,
  requestedBy,
  setRequestedBy,
  name,
  setName,
}: {
  idPrefix: string;
  requestedBy: string;
  setRequestedBy: (v: string) => void;
  name: string;
  setName: (v: string) => void;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <fieldset>
        <legend className="field-label">Who is asking?</legend>
        {[
          ["parent", "Parent or guardian"],
          ["student", "The student"],
        ].map(([value, label]) => (
          <label key={value} className="flex items-center gap-2 text-sm mb-1">
            <input
              type="radio"
              name={`${idPrefix}-requestedBy`}
              value={value}
              checked={requestedBy === value}
              onChange={() => setRequestedBy(value)}
            />
            {label}
          </label>
        ))}
      </fieldset>
      <div>
        <label className="field-label" htmlFor={`${idPrefix}-name`}>
          Your full name
        </label>
        <input
          id={`${idPrefix}-name`}
          className="field-input"
          value={name}
          maxLength={120}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
    </div>
  );
}

function proofNameOk(file: File | null): boolean {
  if (!file) return true;
  const lower = file.name.toLowerCase();
  return PROOF_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/**
 * Round 50 — "Withdraw this application". Collapsed behind a quiet link so it
 * is never the first thing anyone sees; reachable on the confirmation screen
 * and, once a server draft exists, under an in-progress application. Every
 * rule it shows comes from withdrawal.ts, the same module the server enforces.
 */
export function WithdrawPanel({ compact = false, initiallyOpen = false }: { compact?: boolean; initiallyOpen?: boolean }) {
  const { draftToken, applyServerState } = useApplication();
  const cfg = usePublicConfig();
  const [open, setOpen] = useState(initiallyOpen);
  const [requestedBy, setRequestedBy] = useState("");
  const [name, setName] = useState("");
  const [reasonCode, setReasonCode] = useState("");
  const [reasonText, setReasonText] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [proof, setProof] = useState<File | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const nowMs = Date.now();
  if (!draftToken || !withdrawalOpen(nowMs, cfg.sessionEndDate)) return null;

  const deadline = cfg.cancellationDeadline || defaultCancellationDeadline(cfg.sessionStartDate);
  const inTime = withdrawalInTime(nowMs, deadline);
  const deadlineText = formatIsoDate(deadline);
  const reason = withdrawalReason(reasonCode);
  const errors = withdrawalFormErrors({ requestedBy, requesterName: name, reasonCode, reasonText, acknowledged });
  const proofOk = proofNameOk(proof);
  const proofTooBig = !!proof && proof.size > Math.max(1, cfg.maxUploadMb) * 1024 * 1024;
  const valid = Object.keys(errors).length === 0 && proofOk && !proofTooBig;

  if (!open) {
    return (
      <div className={`no-print ${compact ? "mt-2" : "mt-2 mb-6"} text-right`}>
        <button type="button" className="text-xs text-slate-500 underline" onClick={() => setOpen(true)}>
          Withdraw this application
        </button>
      </div>
    );
  }

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError("");
    try {
      const form = new FormData();
      form.set("requestedBy", requestedBy);
      form.set("requesterName", name.trim());
      form.set("reasonCode", reasonCode);
      form.set("reasonText", reasonText.trim());
      form.set("acknowledged", acknowledged ? "true" : "false");
      if (proof) form.set("proof", proof);
      const res = await fetch(`/api/drafts/${encodeURIComponent(draftToken)}/withdraw`, { method: "POST", body: form });
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        status?: string;
        withdrawal?: WithdrawalSummary | null;
      };
      if (!res.ok) {
        setError(body.error || "Could not withdraw right now. Please try again shortly.");
        setConfirming(false);
        return;
      }
      applyServerState("Withdrawn", body.withdrawal ?? null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setError("Could not withdraw right now. Please try again shortly.");
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="section-card border-red-200 no-print" aria-labelledby="withdraw-heading">
      <h2 id="withdraw-heading" className="text-xl font-semibold mb-2">
        Withdraw this application
      </h2>
      <p className="text-sm text-slate-600 mb-3">
        A parent or guardian or the student may withdraw. Nothing is deleted, and you can reinstate the
        application through your link until {formatIsoDate(cfg.amendmentsCloseDate)}.
      </p>
      <div className={`rounded-md border p-3 mb-4 ${inTime ? "border-slate-200 bg-slate-50" : "border-amber-200 bg-amber-50"}`}>
        <p className={`text-sm ${inTime ? "text-slate-700" : "text-amber-900"}`} data-testid="withdraw-timing">
          {inTime
            ? `You are withdrawing on or before ${deadlineText}, the cancellation deadline in the Agreement of Financial Responsibility.`
            : `This is after ${deadlineText}, the cancellation deadline in the Agreement of Financial Responsibility. ` +
              `Under that agreement, the parent or guardian who signed it may be responsible for reimbursing tuition` +
              `${cfg.lateCancellationAmount ? ` (${cfg.lateCancellationAmount})` : ""} unless the cancellation is for a ` +
              `documented emergency. If it is, choose the reason below and attach proof if you have it.`}
        </p>
      </div>

      <RequesterFields
        idPrefix="withdraw"
        requestedBy={requestedBy}
        setRequestedBy={setRequestedBy}
        name={name}
        setName={setName}
      />

      <fieldset className="mt-4">
        <legend className="field-label">Reason</legend>
        {WITHDRAWAL_REASONS.map((r) => (
          <label key={r.code} className="flex items-start gap-2 text-sm mb-1">
            <input
              type="radio"
              name="withdraw-reason"
              value={r.code}
              className="mt-1"
              checked={reasonCode === r.code}
              onChange={() => setReasonCode(r.code)}
            />
            <span>
              {r.label}
              {r.emergency && (
                <span className="text-xs text-slate-500">
                  {" "}
                  (an emergency reason under the Agreement of Financial Responsibility)
                </span>
              )}
            </span>
          </label>
        ))}
      </fieldset>

      <div className="mt-4">
        <label className="field-label" htmlFor="withdraw-text">
          Tell us briefly what happened
        </label>
        <textarea
          id="withdraw-text"
          className="field-input"
          rows={3}
          maxLength={REASON_TEXT_MAX}
          value={reasonText}
          onChange={(e) => setReasonText(e.target.value)}
        />
      </div>

      <div className={`mt-4 ${reason?.emergency ? "rounded-md border border-slate-200 p-3" : ""}`}>
        <label className="field-label" htmlFor="withdraw-proof">
          {reason?.emergency
            ? "If you have proof of the emergency (for example a doctor's note or police report), attach it here. It is not required."
            : "Proof of the emergency, if you have it (optional)"}
        </label>
        <input
          id="withdraw-proof"
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
          onChange={(e) => setProof(e.target.files?.[0] ?? null)}
        />
        {!proofOk && <p className="field-error">The proof must be a PDF, JPG, or PNG file.</p>}
        {proofTooBig && <p className="field-error">The file is larger than {cfg.maxUploadMb} MB.</p>}
        <p className="field-hint">You can also add proof later from this page, or send it to {cfg.supportEmail}.</p>
      </div>

      <p className="text-sm text-slate-700 mt-4">{WRITTEN_NOTICE_TEXT}</p>
      <label className="flex items-start gap-2 text-sm mt-3">
        <input
          type="checkbox"
          className="mt-1"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
        />
        <span>{WITHDRAWAL_ACK_TEXT}</span>
      </label>

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      {!confirming ? (
        <div className="mt-5 flex justify-between gap-3">
          <button type="button" className="btn-secondary" onClick={() => setOpen(false)}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary bg-red-700 hover:bg-red-800"
            disabled={!valid}
            onClick={() => setConfirming(true)}
          >
            Withdraw application
          </button>
        </div>
      ) : (
        <div className="mt-5 rounded-md border border-red-200 bg-red-50 p-3" role="alert">
          <p className="text-sm text-red-900 mb-3">
            Are you sure? The application will be marked withdrawn and both the student and the parent or
            guardian will be emailed.
          </p>
          <div className="flex justify-between gap-3">
            <button type="button" className="btn-secondary" onClick={() => setConfirming(false)} disabled={busy}>
              Go back
            </button>
            <button
              type="button"
              className="btn-primary bg-red-700 hover:bg-red-800"
              disabled={busy}
              onClick={() => void submit()}
            >
              {busy ? "Withdrawing…" : "Yes, withdraw"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
